import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { useWindowSize } from '@hooks/useWindowSize';
import { mdiArrowLeft } from '@mdi/js';
import Icon from '@mdi/react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, Skeleton } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	useGetCompletedOffers,
	useGetOffers,
	useMonitoringOffersStore,
} from 'services/monitoring-offers';
import { chatService } from '../../../services/chat/chatService';
import { MonitoringOngoingCard } from '../components/Card/MonitoringOngoingCard';
import { Empty } from '../components/Empty';

const styles = {
	container: {
		display: 'flex',
		flexDirection: 'column',
		boxShadow: 3,
		position: 'relative',
		bgcolor: 'white',
		borderRadius: 3,
	},
};

export const MonitoringOngoing = () => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const size = useWindowSize();
	const navigate = useNavigate();
	const location = useLocation();

	// Get filter from URL query parameter
	const queryParams = new URLSearchParams(location.search);
	const filterParam = queryParams.get('filter');
	const showCompleted = filterParam === 'completed';

	// Track loading state per monitoring ID
	const [loadingChats, setLoadingChats] = useState<Record<string, boolean>>({});

	const { ongoingOffers, completedOffers } = useMonitoringOffersStore();

	// Fetch offers based on current filter
	const {
		data: ongoingData,
		isLoading: isLoadingOngoing,
		refetch: fetchOngoing,
	} = useGetOffers(showCompleted ? undefined : 'ongoing-project');

	const {
		data: completedData,
		isLoading: isLoadingCompleted,
		refetch: fetchCompleted,
	} = useGetCompletedOffers();

	// Determine current offers and loading state
	const currentOffers = useMemo(() => {
		return showCompleted ? completedOffers : ongoingOffers;
	}, [showCompleted, completedOffers, ongoingOffers]);

	const isLoadingOffers = showCompleted ? isLoadingCompleted : isLoadingOngoing;

	// Handle chat button click with on-demand channel fetching
	const handleChat = useCallback(
		async (monitoringId: string) => {
			// Set loading state for this specific monitoring ID
			setLoadingChats(prev => ({ ...prev, [monitoringId]: true }));
			
			try {
				const channel = await chatService.GetChannelByMonitoringId(monitoringId);
				if (channel?.id) {
					navigateWithLanguage(`/farmer/chats?channelId=${channel.id}`);
				} else {
					console.error('No channel ID found for monitoring offer');
				}
			} catch (error) {
				console.error('Error fetching channel:', error);
			} finally {
				// Clear loading state for this specific monitoring ID
				setLoadingChats(prev => ({ ...prev, [monitoringId]: false }));
			}
		},
		[navigateWithLanguage]
	);

	// Effect to fetch offers when filter changes
	useEffect(() => {
		if (showCompleted) {
			fetchCompleted();
		} else {
			fetchOngoing();
		}
	}, [showCompleted, fetchCompleted, fetchOngoing]);

	usePageTitle(t('monitoring'));
	useAppbarTitle('monitoring');

	const handleToggleCompleted = () => {
		// Update URL with new filter value
		const newFilter = !showCompleted ? 'completed' : 'ongoing';
		navigate({ pathname: location.pathname, search: `?filter=${newFilter}` });
	};

	// Show empty state only if not loading and no data
	const showEmpty = !isLoadingOffers && (!currentOffers || currentOffers.length === 0);

	// Function to check if a specific chat is loading
	const isChatLoading = useCallback((monitoringId: string) => {
		return !!loadingChats[monitoringId];
	}, [loadingChats]);

	return (
		<Box
			sx={{
				...styles.container,
				minHeight: size.width > 768 ? '100%' : 'auto',
			}}
		>
			<div className="flex justify-center items-center">
				<div className="absolute left-[20px] top-[50px]">
					<Button
						className="w-[120px]"
						type="submit"
						onClick={() => navigate(-1)}
						variant="text"
						sx={{ backgroundColor: 'white', color: 'black' }}
						startIcon={<Icon path={mdiArrowLeft} size={1} />}
					>
						{t('back')}
					</Button>
				</div>
				<div
					className="absolute right-[20px] top-[50px] flex items-center justify-center gap-2 cursor-pointer"
					onClick={handleToggleCompleted}
				>
					<CheckCircleOutlineIcon sx={{ color: showCompleted ? 'success.main' : 'inherit' }} />
					<span
						className={`text-base font-bold underline ${showCompleted ? 'text-green-600' : 'text-dark'}`}
					>
						{t('completed')}
					</span>
				</div>
				<h2 className="md:text-2xl font-bold mb-6 mt-5">
					{t(
						showCompleted
							? 'requestsAndOffers.completedProjectsTitle'
							: 'requestsAndOffers.ongoingProjectsTitle'
					)}
				</h2>
			</div>

			<div className="p-[20px]">
				{isLoadingOffers ? (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
						{Array.from({ length: 6 }).map((_, index) => (
							<Box key={index} sx={{ padding: 2, boxShadow: 3, borderRadius: 3, bgcolor: 'white' }}>
								<Skeleton variant="rectangular" height={150} />
								<Skeleton variant="text" sx={{ marginTop: 1, height: 30 }} />
								<Skeleton variant="text" width="80%" />
							</Box>
						))}
					</div>
				) : showEmpty ? (
					<Empty
						title={
							showCompleted
								? t('requestsAndOffers.noCompletedProjectsYet')
								: t('requestsAndOffers.noOngoingProjectsYet')
						}
						buttonTitle={t('monitoringService')}
						route="/farmer/services/monitoring/packages"
						onClick={() => navigateWithLanguage('/farmer/services/monitoring/packages')}
					/>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
						{currentOffers.map((data: any, index: any) => (
							<MonitoringOngoingCard
								key={index}
								data={data}
								onChat={handleChat}
								chatLoading={isChatLoading(data?.id)}
							/>
						))}
					</div>
				)}
			</div>
		</Box>
	);
};
